import React, { useMemo, useState } from 'react';
import { 
  Typography, 
  Card, 
  Row, 
  Col, 
  Button,
  Tag,
  Modal,
  message,
  Spin,
  Space,
  Divider,
  Statistic,
  Descriptions,
  Image,
  Tooltip,
  Alert,
} from 'antd';
import { 
  AppstoreOutlined,
  ShoppingCartOutlined,
  CheckCircleOutlined,
  PlayCircleOutlined,
  ThunderboltOutlined,
  DollarOutlined,
  CustomerServiceOutlined,
  InfoCircleOutlined,
} from '@ant-design/icons';
import { useQuery } from '@tanstack/react-query';
import { supabase } from '../../../shared/lib/supabase';
import { useContractProduct } from '../hooks/useClientProducts';
import type { Product } from '../../../shared/types';
import ProductCheckoutModal from '../components/ProductCheckoutModal';
import { useAuthStore } from '../../../features/auth/store/authStore';

const { Title, Paragraph } = Typography;

export const MarketplacePage: React.FC = () => {
  const [selectedProduct, setSelectedProduct] = useState<Product | null>(null);
  const [isDetailsModalOpen, setIsDetailsModalOpen] = useState(false);
  const [isCheckoutModalOpen, setIsCheckoutModalOpen] = useState(false);
  const [isConfirmRecontractModalOpen, setIsConfirmRecontractModalOpen] = useState(false);
  const { user } = useAuthStore();
  const clientId = user?.id ?? null;

  const currencyValue = (value?: number | string | null) => {
    const numeric = Number(value ?? 0);
    return Number.isFinite(numeric) ? numeric : 0;
  };

  // Fetch enabled products from Supabase - only published products
  const { data: products = [], isLoading } = useQuery({
    queryKey: ['marketplace-products'],
    queryFn: async () => {
      const { data, error } = await supabase
        .from('products')
        .select('*')
        .eq('is_enabled', true)
        .eq('product_type', 'marketplace')
        .eq('marketplace_status', 'published')
        .order('created_at', { ascending: false });
      if (error) throw error;
      return data || [];
    },
  });

  // Fetch contract status for all products
  const { data: productsContractInfo = {} } = useQuery({
    queryKey: ['products-contract-info', clientId],
    enabled: !!clientId,
    queryFn: async () => {
      if (!clientId) return {};
      
      // Fetch all contracts for this client
      const { data: contracts, error: contractsError } = await supabase
        .from('client_products')
        .select('product_id, is_active')
        .eq('client_id', clientId);
      
      if (contractsError) throw contractsError;

      // Fetch all subscriptions for this client
      const { data: subscriptions, error: subsError } = await supabase
        .from('client_subscriptions')
        .select('product_id, status, payment_status')
        .eq('user_id', clientId);
      
      if (subsError) throw subsError;

      // Build info object by product
      const info: Record<string, {
        contractCount: number;
        hasActiveSubscription: boolean;
        hasPendingPayment: boolean;
        hasOverduePayment: boolean;
      }> = {};

      // Track global overdue status
      let hasAnyOverdue = false;

      contracts?.forEach(c => {
        if (!info[c.product_id]) {
          info[c.product_id] = { contractCount: 0, hasActiveSubscription: false, hasPendingPayment: false, hasOverduePayment: false };
        }
        // count only active contracts (ignore historical/inactive client_products)
        if (c.is_active) {
          info[c.product_id].contractCount++;
        }
      });

      subscriptions?.forEach(s => {
        if (!info[s.product_id]) {
          info[s.product_id] = { contractCount: 0, hasActiveSubscription: false, hasPendingPayment: false, hasOverduePayment: false };
        }
        if (s.status === 'active' || s.status === 'pending') {
          info[s.product_id].hasActiveSubscription = true;
        }
        // Only consider pending payment for ACTIVE or PENDING subscriptions, ignore cancelled
        if ((s.status === 'active' || s.status === 'pending') && s.payment_status === 'pending') {
          info[s.product_id].hasPendingPayment = true;
        }
        // Track overdue payments
        if ((s.status === 'active' || s.status === 'pending') && s.payment_status === 'overdue') {
          info[s.product_id].hasOverduePayment = true;
          hasAnyOverdue = true;
        }
      });

      // Store global overdue flag using a special key
      info['__global__'] = {
        contractCount: 0,
        hasActiveSubscription: false,
        hasPendingPayment: false,
        hasOverduePayment: hasAnyOverdue,
      };

      return info;
    },
  });

  // Contract product mutation
  const contractProduct = useContractProduct();

  // Helper to check if product is FREE
  const isProductFree = (product: Product) => {
    return (currencyValue(product.monthly_price) + currencyValue(product.setup_fee)) <= 0;
  };

  // Get product status info
  const getProductStatusInfo = (product: Product) => {
    const info = productsContractInfo[product.id];
    const globalInfo = productsContractInfo['__global__'];
    const isFree = isProductFree(product);
    const isContracted = (info?.contractCount || 0) > 0;
    const hasAnyOverdue = globalInfo?.hasOverduePayment || false;

    // Check if user has any active free products (across all products)
    const hasActiveFreeProduct = Object.entries(productsContractInfo).some(([key, pInfo]) => {
      if (key === '__global__' || key === product.id) return false;
      if ((pInfo?.contractCount || 0) <= 0) return false;
      // Check if this product is free by looking at the products list
      const p = products.find(pr => pr.id === key);
      return p && isProductFree(p);
    });
    
    // Rule: Block paid products if user has overdue payments
    if (!isFree && hasAnyOverdue) {
      return { 
        status: 'blocked', 
        label: 'Bloqueado', 
        color: 'red',
        canContract: false,
        tooltip: 'Você possui assinaturas com pagamento vencido. Regularize antes de contratar novos produtos.'
      };
    }

    // Rule: Only 1 free product active at a time
    if (isFree && !isContracted && hasActiveFreeProduct) {
      return { 
        status: 'blocked', 
        label: 'Limite Atingido', 
        color: 'orange',
        canContract: false,
        tooltip: 'Você já possui um produto gratuito ativo. Cancele-o antes de contratar outro.'
      };
    }
    
    if (isFree && isContracted) {
      return { 
        status: 'blocked', 
        label: 'Já Contratado', 
        color: 'green',
        canContract: false,
        tooltip: 'Produto gratuito já contratado'
      };
    }
    
    if (!isFree && info?.hasPendingPayment) {
      return { 
        status: 'pending', 
        label: 'Contratar', 
        color: 'orange',
        canContract: false,
        tooltip: 'Regularize o pagamento pendente antes de contratar novamente'
      };
    }
    
    if (!isFree && info?.hasActiveSubscription) {
      return { 
        status: 'active', 
        label: 'Assinatura Ativa', 
        color: 'blue',
        canContract: true,
        needsConfirmation: true,
        tooltip: 'Você já possui assinatura ativa. Pode contratar novamente com confirmação.'
      };
    }
    
    if (isContracted) {
      return { 
        status: 'contracted', 
        label: 'Contratado', 
        color: 'green',
        canContract: !isFree,
        tooltip: isFree ? 'Produto gratuito já contratado' : 'Contratar novamente'
      };
    }
    
    return { 
      status: 'available', 
      label: 'Disponível', 
      color: 'default',
      canContract: true
    };
  };

  const handleContract = (product: Product) => {
    const statusInfo = getProductStatusInfo(product);
    
    if (!statusInfo.canContract) {
      message.warning(statusInfo.tooltip || 'Não é possível contratar este produto');
      return;
    }
    
    setSelectedProduct(product);
    
    if (statusInfo.needsConfirmation) {
      setIsConfirmRecontractModalOpen(true);
    } else {
      setIsCheckoutModalOpen(true);
    }
  };

  const handleConfirmRecontract = () => {
    setIsConfirmRecontractModalOpen(false);
    setIsCheckoutModalOpen(true);
  };

  const selectedProductSchemaFields = useMemo(() => {
    if (!selectedProduct?.schema) return [];
    const sections = Array.isArray(selectedProduct.schema.sections)
      ? selectedProduct.schema.sections
      : [];
    return sections.flatMap((section) =>
      (section.fields || []).map((field) => ({
        id: field.id,
        label: field.label,
        type: field.type,
        required: field.required,
        sectionTitle: section.title,
      }))
    );
  }, [selectedProduct]);

  return (
    <div>
      <Title level={2} style={{ marginBottom: 4, background: 'linear-gradient(135deg, #7c3aed, #ec4899)', WebkitBackgroundClip: 'text', WebkitTextFillColor: 'transparent' }}>
        Marketplace
      </Title>
      <Paragraph>
        Navegue pelos produtos disponíveis e contrate workflows para automatizar seus processos.
      </Paragraph>

      {isLoading ? (
        <div style={{ textAlign: 'center', padding: '60px' }}>
          <Spin size="large" />
        </div>
      ) : products.length === 0 ? (
        <Card>
          <div style={{ 
            textAlign: 'center', 
            padding: '60px 20px',
            color: '#999',
          }}>
            <AppstoreOutlined style={{ fontSize: 64, marginBottom: 16 }} />
            <Title level={4} style={{ color: '#999' }}>
              Nenhum produto disponível
            </Title>
            <Paragraph>
              Os produtos habilitados pelo administrador aparecerão aqui.
            </Paragraph>
          </div>
        </Card>
      ) : (
        <Row gutter={[16, 16]}>
          {products.map((product) => {
            const statusInfo = getProductStatusInfo(product);
            const isContracted = statusInfo.status !== 'available';
            return (
              <Col xs={24} sm={12} lg={8} key={product.id}>
                <Card
                  hoverable
                  bodyStyle={{ padding: 16, display: 'flex', flexDirection: 'column', height: 200 }}
                  style={{ 
                    height: 520,
                    borderRadius: 16, 
                    overflow: 'hidden',
                    boxShadow: isContracted ? '0 4px 20px rgba(124, 58, 237, 0.15)' : '0 2px 8px rgba(0,0,0,0.08)',
                    border: isContracted ? '1px solid rgba(124, 58, 237, 0.2)' : '1px solid #f0f0f0',
                    display: 'flex',
                    flexDirection: 'column',
                  }}
                  cover={
                    product.thumbnail_url ? (
                      <div style={{ height: 200, overflow: 'hidden', display: 'flex', alignItems: 'center', justifyContent: 'center', background: '#f5f7f9', position: 'relative' }}>
                        {/* Status badges no canto superior */}
                        
                        <Image
                            src={product.thumbnail_url}
                            alt={product.name}
                            title={product.name}
                            style={{ maxHeight: '100%', width: '100%', objectFit: 'cover' }}
                            preview={false}
                            fallback="/public/placeholder-product.png"
                          />
                          {product.video_url && (
                            <a
                              href={product.video_url}
                              target="_blank"
                              rel="noopener noreferrer"
                              onClick={(e) => { e.stopPropagation(); }}
                              aria-label={`Watch demo for ${product.name}`}
                              style={{ position: 'absolute', right: 10, bottom: 10 }}
                            >
                              <Button shape="circle" size="large" icon={<PlayCircleOutlined />} style={{ background: 'rgba(0,0,0,0.6)', color: '#fff', border: 'none' }} />
                            </a>
                          )}
                        </div>
                      ) : (
                        <div style={{ height: 200, display: 'flex', alignItems: 'center', justifyContent: 'center', background: '#fafafa' }}>
                          <AppstoreOutlined style={{ fontSize: 48, color: '#d9d9d9' }} />
                        </div>
                      )
                    }
                    actions={[
                      <Button
                        key="support"
                        type="default"
                        icon={<CustomerServiceOutlined />}
                        onClick={(event) => {
                          event.stopPropagation();
                          window.location.href = `/client/support?product=${product.id}`;
                        }}
                      >
                        Suporte
                      </Button>,
                      <Button
                        key="details"
                        type="default"
                        icon={<AppstoreOutlined />}
                        onClick={(event) => {
                          event.stopPropagation();
                          setSelectedProduct(product);
                          setIsDetailsModalOpen(true);
                        }}
                      >
                        Detalhes
                      </Button>,
                      <Tooltip key="contract-tooltip" title={statusInfo.tooltip}>
                        <Button
                          key="contract"
                          type={!statusInfo.canContract ? 'default' : 'primary'}
                          icon={!statusInfo.canContract ? <CheckCircleOutlined /> : <ShoppingCartOutlined />}
                          onClick={(event) => {
                            event.stopPropagation();
                            handleContract(product);
                          }}
                          disabled={!statusInfo.canContract}
                        >
                          {!statusInfo.canContract 
                            ? statusInfo.label 
                            : (statusInfo.needsConfirmation ? 'Recontratar' : 'Contratar')}
                        </Button>
                      </Tooltip>,
                    ]}
                  >
                    <div style={{ display: 'flex', flexDirection: 'column', height: '100%' }}>
                      {/* Header com título e preço */}
                      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: 12, marginBottom: 12 }}>
                        <div style={{ flex: 1 }}>
                          <Typography.Text strong style={{ display: 'block', fontSize: 16, lineHeight: 1.4 }}>
                            {product.name}
                          </Typography.Text>
                          {product.category && (
                            <div style={{ marginTop: 6 }}>
                              <Tag color="purple" style={{ fontSize: 11 }}>{product.category}</Tag>
                            </div>
                          )}
                        </div>
                        <div style={{ textAlign: 'right', minWidth: 85 }}>
                          {currencyValue(product.monthly_price) + currencyValue(product.setup_fee) <= 0 ? (
                            <div>
                              <div style={{ fontSize: 16, fontWeight: 700, color: '#52c41a' }}>GRÁTIS</div>
                              <div style={{ fontSize: 11, color: '#666' }}>sem custos</div>
                            </div>
                          ) : (
                            <>
                              {product.monthly_price !== undefined && product.monthly_price !== null && (
                                <div>
                                  <div style={{ fontSize: 16, fontWeight: 700, color: '#111' }}>
                                    {currencyValue(product.monthly_price).toLocaleString('pt-BR', { style: 'currency', currency: 'BRL' })}
                                  </div>
                                  <div style={{ fontSize: 11, color: '#666' }}>por mês</div>
                                </div>
                              )}
                            </>
                          )}
                        </div>
                      </div>

                      {/* Descrição */}
                      <div style={{ flex: 1, overflow: 'hidden' }}>
                        <Paragraph 
                          type="secondary" 
                          ellipsis={{ rows: 3 }} 
                          style={{ margin: 0, fontSize: 13, lineHeight: 1.5 }}
                        >
                          {product.short_description || product.description || '-'}
                        </Paragraph>
                      </div>

                      {/* Footer com trial e setup fee - não exibir para produtos gratuitos */}
                      {!isProductFree(product) && (product.trial_days || (product.setup_fee && product.setup_fee > 0)) && (
                        <div style={{ marginTop: 12, paddingTop: 12, borderTop: '1px solid #f0f0f0' }}>
                          <Space direction="vertical" size={4} style={{ width: '100%' }}>
                            {typeof product.trial_days === 'number' && product.trial_days >= 1 && (
                              <Tag color="blue" icon={<ThunderboltOutlined />}>
                                Trial {product.trial_days} {product.trial_days === 1 ? 'dia' : 'dias'}
                              </Tag>
                            )}
                            {product.setup_fee && product.setup_fee > 0 && (
                              <Tag color="orange" icon={<InfoCircleOutlined />}>
                                Taxa de setup: {currencyValue(product.setup_fee).toLocaleString('pt-BR', { style: 'currency', currency: 'BRL' })}
                              </Tag>
                            )}
                          </Space>
                        </div>
                      )}
                    </div>
                  </Card>
                </Col>
              );
          })}
        </Row>
      )}

      {/* Modal de confirmação para recontratar produto pago */}
      <Modal
        title="Confirmar nova contratação"
        open={isConfirmRecontractModalOpen}
        onCancel={() => {
          setIsConfirmRecontractModalOpen(false);
          setSelectedProduct(null);
        }}
        footer={[
          <Button key="cancel" onClick={() => {
            setIsConfirmRecontractModalOpen(false);
            setSelectedProduct(null);
          }}>
            Cancelar
          </Button>,
          <Button
            key="confirm"
            type="primary"
            icon={<ShoppingCartOutlined />}
            onClick={handleConfirmRecontract}
          >
            Sim, contratar novamente
          </Button>,
        ]}
      >
        <Space direction="vertical" size="middle" style={{ width: '100%' }}>
          <Alert
            type="warning"
            showIcon
            message="Você já possui uma assinatura ativa deste produto"
            description="Ao contratar novamente, uma nova cobrança será gerada. Deseja continuar?"
          />
          {selectedProduct && (
            <div>
              <Typography.Text strong>Produto:</Typography.Text> {selectedProduct.name}
              <br />
              <Typography.Text strong>Valor:</Typography.Text> {currencyValue(selectedProduct.monthly_price).toLocaleString('pt-BR', { style: 'currency', currency: 'BRL' })}/mês
            </div>
          )}
        </Space>
      </Modal>

      <Modal
        title={selectedProduct ? `• ${selectedProduct.name}` : 'Detalhes do Workflow'}
        open={isDetailsModalOpen}
        onCancel={() => {
          setIsDetailsModalOpen(false);
          setSelectedProduct(null);
        }}
        width={820}
        footer={[
          <Button key="close" onClick={() => setIsDetailsModalOpen(false)}>
            Fechar
          </Button>,
          selectedProduct && (() => {
            const statusInfo = getProductStatusInfo(selectedProduct);
            return (
              <Tooltip
                key="contract"
                title={!statusInfo.canContract ? statusInfo.tooltip : undefined}
              >
                <Button
                  type="primary"
                  icon={<ShoppingCartOutlined />}
                  disabled={!statusInfo.canContract}
                  loading={contractProduct.isPending}
                  onClick={() => handleContract(selectedProduct)}
                >
                  {!statusInfo.canContract 
                    ? statusInfo.label 
                    : (statusInfo.needsConfirmation ? 'Contratar Novamente' : 'Contratar workflow')}
                </Button>
              </Tooltip>
            );
          })(),
        ]}
      >
        {selectedProduct ? (
          <Space direction="vertical" size="large" style={{ width: '100%' }}>
            {/* Status alert for contracted products */}
            {(() => {
              const statusInfo = getProductStatusInfo(selectedProduct);
              if (statusInfo.status === 'blocked') {
                return (
                  <Alert
                    type="success"
                    showIcon
                    message="Produto gratuito já contratado"
                    description="Este produto gratuito já foi contratado e não pode ser contratado novamente."
                  />
                );
              }
              if (statusInfo.status === 'pending') {
                return (
                  <Alert
                    type="warning"
                    showIcon
                    message="Pagamento pendente"
                    description="Há um pagamento pendente para este produto. Regularize antes de contratar novamente."
                  />
                );
              }
              if (statusInfo.status === 'active') {
                return (
                  <Alert
                    type="info"
                    showIcon
                    message="Assinatura ativa"
                    description="Você já possui uma assinatura ativa deste produto. Pode contratar novamente, mas uma nova cobrança será gerada."
                  />
                );
              }
              return null;
            })()}
            
            {selectedProduct.thumbnail_url && (
              <Image
                src={selectedProduct.thumbnail_url}
                alt={selectedProduct.name}
                style={{ borderRadius: 12, maxHeight: 240, objectFit: 'cover' }}
                preview={false}
              />
            )}

            <Space size="large" wrap>
              {currencyValue(selectedProduct.monthly_price) + currencyValue(selectedProduct.setup_fee) <= 0 ? (
                <div style={{
                  padding: '16px 24px',
                  background: '#f6ffed',
                  border: '2px solid #b7eb8f',
                  borderRadius: 8,
                  textAlign: 'center'
                }}>
                  <div style={{ fontSize: 24, fontWeight: 700, color: '#52c41a' }}>✨ GRÁTIS</div>
                  <div style={{ fontSize: 14, color: '#52c41a', marginTop: 4 }}>Sem custos de ativação ou mensalidade</div>
                </div>
              ) : (
                <>
                  <Statistic
                    title="Preço mensal"
                    value={currencyValue(selectedProduct.monthly_price)}
                    prefix={<DollarOutlined />}
                    precision={2}
                  />
                  {selectedProduct.setup_fee && selectedProduct.setup_fee > 0 && (
                    <Statistic
                      title="Taxa de setup"
                      value={currencyValue(selectedProduct.setup_fee)}
                      prefix={<DollarOutlined />}
                      precision={2}
                    />
                  )}
                </>
              )}
              {!isProductFree(selectedProduct) && selectedProduct.trial_days && selectedProduct.trial_days >= 1 && (
                <Statistic
                  title="Período de trial"
                  value={selectedProduct.trial_days}
                  suffix={selectedProduct.trial_days === 1 ? "dia" : "dias"}
                  prefix={<ThunderboltOutlined />}
                />
              )}
            </Space>

            {/* Explicação sobre taxa de setup */}
            {selectedProduct.setup_fee && selectedProduct.setup_fee > 0 && (
              <Alert
                type="info"
                showIcon
                icon={<InfoCircleOutlined />}
                message="Sobre a Taxa de Setup"
                description="A taxa de setup é cobrada uma única vez quando você necessitar de ajuda especializada para configuração do produto, como por exemplo, a criação de credenciais para APIs (Google Calendar, Drive, etc.) ou configurações avançadas."
              />
            )}

            <Descriptions column={1} bordered size="small">
              {selectedProduct.category && (
                <Descriptions.Item label="Categoria">
                  <Tag color="purple">{selectedProduct.category}</Tag>
                </Descriptions.Item>
              )}
              {selectedProduct.n8n_instance?.name && (
                <Descriptions.Item label="Instância n8n">
                  <Tag color="blue">{selectedProduct.n8n_instance.name}</Tag>
                </Descriptions.Item>
              )}
              {selectedProduct.video_url && (
                <Descriptions.Item label="Demo">
                  <Button
                    type="link"
                    icon={<PlayCircleOutlined />}
                    href={selectedProduct.video_url}
                    target="_blank"
                    rel="noopener noreferrer"
                  >
                    Assistir demonstração
                  </Button>
                </Descriptions.Item>
              )}
            </Descriptions>

            {(selectedProduct.full_description || selectedProduct.description) && (
              <div>
                <Title level={4}>Como funciona</Title>
                <div
                  style={{ color: '#4b5563', lineHeight: 1.6 }}
                  dangerouslySetInnerHTML={{
                    __html: selectedProduct.full_description || selectedProduct.description || '',
                  }}
                />
              </div>
            )}

            {selectedProductSchemaFields.length > 0 && (
              <div>
                <Divider orientation="left">Variáveis configuráveis</Divider>
                <Row gutter={[12, 12]}>
                  {selectedProductSchemaFields.map((field) => (
                    <Col xs={24} sm={12} key={field.id}>
                      <Card size="small" bordered>
                        <Typography.Text strong>{field.label}</Typography.Text>
                        <div style={{ display: 'flex', gap: 8 }}>
                          <Tag color="default">{field.type}</Tag>
                          {field.required && <Tag color="red">Obrigatório</Tag>}
                        </div>
                        {field.sectionTitle && (
                          <Typography.Text type="secondary" style={{ fontSize: 12 }}>
                            Seção: {field.sectionTitle}
                          </Typography.Text>
                        )}
                      </Card>
                    </Col>
                  ))}
                </Row>
              </div>
            )}

            <Paragraph type="secondary">
              Após contratar o workflow, você poderá preencher o formulário e configurar as APIs necessárias.
            </Paragraph>
          </Space>
        ) : (
          <Paragraph>Selecione um workflow para visualizar os detalhes.</Paragraph>
        )}
      </Modal>

      <ProductCheckoutModal
        product={selectedProduct}
        open={isCheckoutModalOpen}
        onClose={() => {
          setIsCheckoutModalOpen(false);
          setSelectedProduct(null);
        }}
      />
    </div>
  );
};
